/**
 * Controle do CROMO DO APP (o menu principal) a partir da tela que está aberta.
 *
 * Duas telas precisam disso, e nenhuma delas tem acesso ao `SidebarLayout` que
 * as hospeda:
 *
 * - os **autos** pedem o menu oculto por completo enquanto estão montados — a
 *   largura vai para a leitura, e o menu abre por cima pelo botão do cabeçalho
 *   (`docs/PLANO_BANCADA.md`, Bloco 1; antes era só recolhido, Bloco 5.3 do
 *   `PLANO_UI_AUTOS.md`);
 * - as **telas de trabalho** pedem o card de moldura colado — sem vão, borda nem
 *   sombra — para usar a largura inteira (docs/PLANO_MOLDURA.md).
 *
 * O pedido vale só ENQUANTO A TELA ESTÁ MONTADA: sair da rota devolve o menu ao
 * estado que ele tinha no resto do app. Um pedido por vez — só há uma tela
 * montada de cada vez, e empilhar requisitantes seria máquina para um problema
 * que não existe.
 *
 * O valor padrão do contexto é inerte de propósito: `SlimLayout` (modo janela)
 * não tem menu nenhum, e as telas testadas isoladamente não precisam de
 * provider para montar.
 */
import { createContext, useContext, useLayoutEffect } from 'react';

export interface CromoApp {
  /** Esconde o menu por inteiro (autos). */
  pedirOcultacao: (oculto: boolean) => void;
  /**
   * Abre o menu por cima da tela. Só existe ENQUANTO o menu está oculto — é o
   * caminho de volta a ele; ausente, não há o que abrir (menu visível, ou o
   * `SlimLayout`, que não tem menu).
   */
  abrirMenu?: () => void;
  /** Cola o card de moldura nas bordas (sem vão, borda, raio nem sombra). */
  pedirColagem: (colado: boolean) => void;
  /**
   * Tira também o respiro do contêiner: a tela vai de ponta a ponta e cuida do
   * próprio espaçamento (os autos, para a faixa do topo encostar nas bordas).
   */
  pedirSemRespiro: (semRespiro: boolean) => void;
}

const INERTE: CromoApp = {
  pedirOcultacao: () => {},
  pedirColagem: () => {},
  pedirSemRespiro: () => {},
};

export const CromoAppContext = createContext<CromoApp>(INERTE);

/**
 * Esconde o menu enquanto `ativo` e a tela estiver montada. Antes da pintura,
 * como a colagem: senão o menu aparece e some ao abrir a tela, e o split dos
 * autos mede duas larguras.
 */
export function useMenuOculto(ativo: boolean): void {
  const { pedirOcultacao } = useContext(CromoAppContext);
  useLayoutEffect(() => {
    pedirOcultacao(ativo);
    return () => pedirOcultacao(false);
  }, [ativo, pedirOcultacao]);
}

/** Abre o menu oculto por cima da tela; `undefined` quando não há o que abrir. */
export function useAbrirMenuDoApp(): (() => void) | undefined {
  return useContext(CromoAppContext).abrirMenu;
}

/**
 * Card de moldura colado enquanto a tela estiver montada — telas de trabalho. O
 * conteúdo fica a 12px do topo e do rodapé e a 16px dos lados (`SidebarLayout`), e a
 * altura das telas de altura fixa desconta isso. Pedido antes da pintura, senão o
 * card aparece e some ao abrir a tela.
 */
export function usePainelColado(ativo = true): void {
  const { pedirColagem } = useContext(CromoAppContext);
  useLayoutEffect(() => {
    pedirColagem(ativo);
    return () => pedirColagem(false);
  }, [ativo, pedirColagem]);
}

/**
 * Sem o respiro do contêiner enquanto a tela estiver montada: ela vai de ponta a
 * ponta (os autos: a faixa do topo encosta nas bordas, como uma tela própria) e
 * cuida do próprio espaçamento. Antes da pintura, pelo mesmo motivo da colagem.
 */
export function useSemRespiro(ativo = true): void {
  const { pedirSemRespiro } = useContext(CromoAppContext);
  useLayoutEffect(() => {
    pedirSemRespiro(ativo);
    return () => pedirSemRespiro(false);
  }, [ativo, pedirSemRespiro]);
}
